/**
 * AutoUI Behavior
 *
 * Automatically discovers labeled Properties and behavior actions from child
 * SceneObjects and renders them as an interactive UI panel using Babylon.js GUI.
 */

import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Observer } from '@babylonjs/core/Misc/observable';
import { Scene } from '@babylonjs/core/scene';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { StackPanel } from '@babylonjs/gui/2D/controls/stackPanel';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import { Button } from '@babylonjs/gui/2D/controls/button';
import { Checkbox } from '@babylonjs/gui/2D/controls/checkbox';
import { InputText } from '@babylonjs/gui/2D/controls/inputText';
import { Slider } from '@babylonjs/gui/2D/controls/sliders/slider';
import { ColorPicker } from '@babylonjs/gui/2D/controls/colorpicker';
import { Control } from '@babylonjs/gui/2D/controls/control';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { TraceBehavior } from './tracebehavior';
import { AutoUIData } from '../scenedata/behaviors/autouidata';
import { Properties, PropertyValue } from './properties';
import { Keyframes } from './keyframes';
import { Pages } from './pages';
import type { PropertyDeclaration, NumberDeclaration } from '../scenedata/behaviors/propertiesdata';
import { declarationUnits, unitSuffix, fromCanonical } from '../core/canopy/units';
import { formatSliderNumber } from './autoui-number';
import { GENERATED_BEHAVIOR_SCHEMAS } from '../scenedata/schemas/generated';
import type { ActionSchema, BehaviorSchema } from '../scenedata/schemas/types';
import type { SceneObject } from '../core/SceneObject';
import { Logger } from '../utils/Logger';
import { SceneCamera } from '../core/Camera';

/** Pre-built lookup map: behavior type → schema (avoids linear scan per frame) */
const SCHEMA_BY_TYPE: ReadonlyMap<string, BehaviorSchema> = new Map(
  GENERATED_BEHAVIOR_SCHEMAS.map(s => [s.type, s])
);

/**
 * Editor-injected commit for AutoUI variant-swatch picks made in EDIT mode.
 * AutoUI is runtime code and cannot import `src/editor`; a host (the sandbox)
 * installs this so an edit-mode swatch pick routes into the active state (or the
 * declaration base at Base) with undo — parity with the behaviors panel — instead
 * of a runtime-tier `setValue`. In play mode the swatch always uses `setValue`.
 *
 * SCOPE: this is a process-global, single-host hook (like `SceneObject`'s
 * `_applyComponents` late-binding). It assumes ONE editor session per process —
 * true for trace-sandbox (a single imperative `TraceSandbox`). It is NOT scoped
 * per `TraceScene`/`TraceViewer`, so it is unsuitable for a page embedding two
 * concurrent editor sessions; such a host must scope routing itself (or leave the
 * hook null, in which case every swatch pick falls back to runtime `setValue`).
 * Pure runtime viewers never install it and are unaffected.
 */
type SwatchEditorCommit = (source: SceneObject, propName: string, value: PropertyValue) => void;
let swatchEditorCommit: SwatchEditorCommit | null = null;

/** Install (or clear with `null`) the edit-mode swatch commit hook. See caveats above. */
export function setAutoUISwatchEditorCommit(commit: SwatchEditorCommit | null): void {
  swatchEditorCommit = commit;
}

/** A discovered action with a bound execute function */
interface DiscoveredAction {
  name: string;
  label: string;
  execute: () => void;
}

/** Discovered items from a single SceneObject */
interface DiscoveredItem {
  source: SceneObject;
  properties: PropertyDeclaration[];
  actions: DiscoveredAction[];
}

/** Tracked control for live value updates */
interface TrackedControl {
  source: SceneObject;
  propName: string;
  propType: string;
  update: (value: PropertyValue | undefined) => void;
}

/**
 * Runtime behavior that creates an automatic UI for controlling
 * labeled properties and triggering labeled interactions.
 */
export class AutoUI extends TraceBehavior<AutoUIData> {
  static readonly behaviorType = "AutoUI" as const;

  private advancedTexture: AdvancedDynamicTexture | null = null;
  private rootPanel: Rectangle | null = null;
  private readoutControl: TextBlock | null = null;
  private panelCollapsed: boolean | undefined;
  private updateCollapse: (() => void) | null = null;
  private worldSpacePlane: Mesh | null = null;

  /** Tracked controls for live value updates */
  private trackedControls: TrackedControl[] = [];

  /** Observer for frame updates */
  private renderObserver: Observer<Scene> | null = null;

  /** Observer that polls for structure changes (new Properties/actions) */
  private structureObserver: Observer<Scene> | null = null;

  /** Fingerprint of the last-built UI structure, for detecting changes */
  private structureFingerprint = '';

  /** Safe area change listener */
  private _safeAreaHandler: ((e: Event) => void) | null = null;

  /** Last known CSS width, for updating idealWidth on resize */
  private _lastCssWidth = 0;

  /** Cached discovery root (resolved once on attach, reused for polling) */
  private _discoveryRoot: SceneObject | null = null;

  // ============================================================================
  // LIFECYCLE
  // ============================================================================

  init(): void {
    // Initialization happens in onAttach when we have access to the scene
  }

  protected onAttach(_target: TransformNode): void {
    // Loading may resolve bindings before attachment. Never leave an earlier
    // GUI texture behind when attachment establishes the live inspector.
    this.disposeUI();
    this._discoveryRoot = this.resolveDiscoveryRoot();
    this.buildUI();
    // Poll for structure changes — handles behaviors added after AutoUI
    // (e.g. Properties added later in the editor) and property list edits.
    this.startStructurePolling();
    this._subscribeSafeArea();
  }

  protected onDetach(): void {
    this._unsubscribeSafeArea();
    this.stopStructurePolling();
    this.disposeUI();
  }

  protected async applyCommit(_patch: Partial<AutoUIData>): Promise<void> {
    if (!this.attachedNode) return;
    // A live readout can change every tick. Updating it must not rebuild the
    // GUI texture, lose slider drags, or reset the viewer's collapse choice.
    // Presence, not truthiness: a bound readout legitimately resolves to ''.
    if (Object.keys(_patch).every(key => key === 'readout') && this.readoutControl && this._resolved.readout !== undefined) {
      this.readoutControl.text = this._resolved.readout;
      return;
    }
    if ('collapsible' in _patch || 'collapseBelowWidth' in _patch) this.panelCollapsed = undefined;
    this._discoveryRoot = this.resolveDiscoveryRoot();
    this.disposeUI();
    this.buildUI();
    this.structureFingerprint = this.computeStructureFingerprint();
  }

  // ============================================================================
  // DISCOVERY
  // ============================================================================

  /**
   * Discover all labeled properties and behavior actions from descendant SceneObjects
   */
  private discoverLabeledItems(): DiscoveredItem[] {
    const results: DiscoveredItem[] = [];
    const descendants = this.getAllDescendantSceneObjects();

    // Include owner properties alongside its actions, once. An explicit
    // discovery root still includes host actions (existing contract), but does
    // not expose unrelated host properties outside that root.
    const descendantSet = new Set(descendants);
    const candidates = descendantSet.has(this.sceneObject)
      ? descendants : [this.sceneObject, ...descendants];
    for (const child of new Set(candidates)) {
      const propertiesBehavior = child.getBehavior(Properties);
      // `hidden` declarations are driven values (page-selected heights,
      // compiled offsets), not controls — declared and bindable, never a slider.
      const props = descendantSet.has(child)
        ? (propertiesBehavior?.getProperties() ?? []).filter(p => !p.hidden) : [];
      const actions = this.discoverActions(child);

      if (props.length > 0 || actions.length > 0) {
        results.push({
          source: child,
          properties: props,
          actions,
        });
      }
    }

    Logger.debug(`[AutoUI] Discovered ${results.length} objects with labeled items`);
    return results;
  }

  /**
   * Discover callable actions from a SceneObject's behaviors.
   * Uses the @action schema metadata to find actions, then binds
   * them to the typed behavior methods.
   */
  private discoverActions(obj: SceneObject): DiscoveredAction[] {
    const actions: DiscoveredAction[] = [];

    for (const behavior of obj.getBehaviors()) {
      const schema = SCHEMA_BY_TYPE.get(behavior.name);
      if (!schema?.actions) continue;

      for (const actionDef of schema.actions) {
        const bound = this.bindAction(obj, behavior.name, actionDef);
        if (bound) actions.push(bound);
      }
    }

    return actions;
  }

  /**
   * Bind an action schema entry to a typed method call on the behavior.
   * Returns null if the behavior/action combination is not supported.
   */
  private bindAction(obj: SceneObject, behaviorType: string, actionDef: ActionSchema): DiscoveredAction | null {
    switch (behaviorType) {
      case Pages.behaviorType: {
        const pages = obj.getBehavior(Pages);
        if (!pages) return null;
        if (actionDef.name === 'next') {
          return { name: 'next', label: actionDef.label, execute: () => { void pages.next(); } };
        }
        if (actionDef.name === 'prev') {
          return { name: 'prev', label: actionDef.label, execute: () => { void pages.prev(); } };
        }
        // showAllPages is an editor/overview action, and showPage requires a page
        // index parameter — neither is surfaced as an AutoUI button
        return null;
      }
      case Keyframes.behaviorType: {
        const kf = obj.getBehavior(Keyframes);
        if (!kf) return null;
        if (actionDef.name === 'next') {
          return { name: 'next', label: actionDef.label, execute: () => { void kf.next(); } };
        }
        if (actionDef.name === 'prev') {
          return { name: 'prev', label: actionDef.label, execute: () => { void kf.prev(); } };
        }
        // goTo requires a keyframe name parameter — not suitable for AutoUI buttons
        return null;
      }
      default:
        return null;
    }
  }

  /**
   * Resolve the discovery root object from propertyGroups config.
   * Called once on attach; result is cached in _discoveryRoot.
   */
  private resolveDiscoveryRoot(): SceneObject {
    const traceScene = this.sceneObject.traceScene;

    if (this._resolved.propertyGroups && this._resolved.propertyGroups.length > 0) {
      const targetName = this._resolved.propertyGroups[0];
      // Try self as context first, then parent — AutoUI is commonly a child
      // of the target (e.g. PGA scene: AutoUI child of ESPN_PGA_Scene)
      const targetObject = traceScene.resolveSceneObject(targetName, this.sceneObject)
        ?? traceScene.resolveSceneObject(targetName, this.sceneObject.getParent() ?? undefined);
      if (targetObject) {
        Logger.debug(`[AutoUI] Using propertyGroups root: "${targetName}"`);
        return targetObject;
      }
      // Fall back to parent if available (common pattern: AutoUI is child of target)
      const parent = this.sceneObject.getParent();
      if (parent) {
        Logger.warn(`[AutoUI] propertyGroups target "${targetName}" not found, falling back to parent "${parent.name}"`);
        return parent;
      }
      Logger.warn(`[AutoUI] propertyGroups target "${targetName}" not found, falling back to self`);
    }

    return this.sceneObject;
  }

  /**
   * Get the discovery root and all its descendants.
   * Uses cached root resolved on attach to avoid repeated resolution + log spam.
   */
  private getAllDescendantSceneObjects(): SceneObject[] {
    if (!this._discoveryRoot) {
      this._discoveryRoot = this.resolveDiscoveryRoot();
    }
    return [this._discoveryRoot, ...this._discoveryRoot.getDescendants()];
  }

  // ============================================================================
  // UI BUILDING
  // ============================================================================

  /**
   * Build the complete UI based on discovered items.
   * @returns true if items were discovered and UI was built, false otherwise.
   */
  private buildUI(): boolean {
    // Native headless probes run the scene without a Canvas2D renderer. Keep
    // authored controls intact, but leave GUI rendering to a real viewer.
    if (!this.sceneObject.babylonScene.getEngine().getRenderingCanvas()) return false;
    const discovered = this.discoverLabeledItems();
    if (discovered.length === 0 && this._resolved.readout === undefined && !this._resolved.cameraViews?.length) {
      Logger.debug('[AutoUI] No labeled properties or interactions found');
      return false;
    }

    // Create the advanced texture based on render mode
    this.createAdvancedTexture();
    if (!this.advancedTexture) return false;

    // Create the root panel container
    const width = this._resolved.width ?? 300;
    this.rootPanel = this.createRootPanel(width);
    this.advancedTexture.addControl(this.rootPanel);

    // Create inner stack panel for content
    const contentPanel = new StackPanel('autoui-content');
    contentPanel.width = '100%';
    contentPanel.paddingTop = '10px';
    contentPanel.paddingBottom = '10px';
    contentPanel.paddingLeft = '10px';
    contentPanel.paddingRight = '10px';
    this.rootPanel.addControl(contentPanel);

    // Collapsing is viewer state, not an authored property mutation. This
    // allows a phone-sized inspector to expose every control without covering
    // the mechanism when closed.
    const body = new StackPanel('autoui-body');
    body.width = '100%';
    if (this._resolved.collapsible) {
      const engine = this.sceneObject.babylonScene.getEngine();
      this.panelCollapsed ??= engine.getRenderWidth() * engine.getHardwareScalingLevel() < (this._resolved.collapseBelowWidth ?? 0);
      const toggle = Button.CreateSimpleButton('autoui-toggle', '');
      toggle.height = '36px'; toggle.width = '100%'; toggle.thickness = 0;
      toggle.color = '#EEF5F8'; toggle.background = 'transparent'; toggle.fontSize = 16;
      toggle.hoverCursor = 'pointer';
      const update = () => {
        body.isVisible = !this.panelCollapsed;
        toggle.textBlock!.text = `${this._resolved.title ?? 'Controls'}  ${this.panelCollapsed ? '+' : '−'}`;
      };
      toggle.onPointerClickObservable.add(() => { this.panelCollapsed = !this.panelCollapsed; update(); });
      this.updateCollapse = update;
      update(); contentPanel.addControl(toggle);
    } else if (this._resolved.title) {
      const titleWrapper = new Rectangle('autoui-title-wrapper');
      titleWrapper.width = '100%';
      titleWrapper.adaptHeightToChildren = true;
      titleWrapper.thickness = 0;
      titleWrapper.paddingBottom = '15px';

      const title = this.createLabel(this._resolved.title, 18, true);
      title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      titleWrapper.addControl(title);
      contentPanel.addControl(titleWrapper);
    }

    contentPanel.addControl(body);

    if (this._resolved.cameraViews?.length) {
      const row = new Rectangle('autoui-views');
      row.thickness = 0; row.width = '100%'; row.height = '34px';
      const views = this._resolved.cameraViews;
      views.forEach((view, index) => {
        const button = Button.CreateSimpleButton(`autoui-view-${index}`, view.label);
        button.width = `${100 / views.length}%`; button.height = '30px';
        button.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        button.left = `${100 * index / views.length}%`;
        button.paddingRight = '3px'; button.background = '#284A59'; button.color = '#EEF5F8';
        button.thickness = 0; button.fontSize = 12; button.hoverCursor = 'pointer';
        button.onPointerClickObservable.add(() => {
          const camera = this.sceneObject.traceScene.getCamera();
          if (!camera || !Number.isFinite(view.yaw) || !Number.isFinite(view.pitch)) return;
          const target = view.target === undefined ? this.sceneObject
            : this.sceneObject.traceScene.resolveSceneObject(view.target, this.sceneObject);
          const radiusScale = view.radiusScale ?? 1.1;
          // Explicit bad references must not unexpectedly frame the entire exhibit.
          if (!target || !Number.isFinite(radiusScale) || radiusScale < 1) return;
          if (!SceneCamera.focusOnNode(camera, target.transform, { radiusScale, framingTime: 0 }, this.sceneObject.traceScene.safeAreaInsets)) return;
          camera.inertialAlphaOffset = camera.inertialBetaOffset = camera.inertialRadiusOffset = 0;
          camera.inertialPanningX = camera.inertialPanningY = 0;
          camera.alpha = view.yaw * Math.PI / 180;
          camera.beta = Math.max(.01, Math.min(Math.PI - .01, view.pitch * Math.PI / 180));
          if (this._resolved.collapsible && this._resolved.collapseOnView) {
            this.panelCollapsed = true;
            this.updateCollapse?.();
          }
        });
        row.addControl(button);
      });
      body.addControl(row);
    }

    // Add controls for each discovered object
    for (const item of discovered) {
      this.addObjectSection(body, item);
    }

    if (this._resolved.readout !== undefined) {
      const readout = this.createLabel(this._resolved.readout, 13);
      readout.name = 'autoui-readout'; readout.color = '#DCE7ED';
      readout.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      readout.paddingTop = '14px'; readout.paddingBottom = '6px';
      readout.textWrapping = true; readout.resizeToFit = true;
      readout.isHitTestVisible = false;
      this.readoutControl = readout; body.addControl(readout);
    }

    // Start updating tracked controls on each frame
    this.startFrameUpdates();
    return true;
  }

  /**
   * Create the AdvancedDynamicTexture based on renderMode
   */
  private createAdvancedTexture(): void {
    const scene = this.sceneObject.babylonScene;
    const renderMode = this._resolved.renderMode ?? 'ScreenSpaceOverlay';

    // When adaptToDeviceRatio is on, the texture renders at device-pixel
    // resolution (e.g. 2x on Retina). Setting idealWidth to the CSS-pixel
    // width makes idealRatio compensate so pixel-based GUI sizes stay
    // visually correct. We leave renderAtIdealSize OFF (the default) so
    // pointer coordinates remain in texture-space — this avoids the
    // Slider coordinate mismatch that adaptiveScaling / renderAtIdealSize
    // introduces.
    const engine = scene.getEngine();
    const cssWidth = engine.getRenderWidth() * engine.getHardwareScalingLevel();

    switch (renderMode) {
      case 'ScreenSpaceOverlay':
      case 'ScreenSpaceCamera':
        this.advancedTexture = AdvancedDynamicTexture.CreateFullscreenUI(
          'autoui-texture',
          true,
          scene,
        );
        this.advancedTexture.idealWidth = cssWidth;
        this._lastCssWidth = cssWidth;
        break;

      case 'WorldSpace': {
        // Create a plane to attach the UI to
        this.worldSpacePlane = MeshBuilder.CreatePlane(
          'autoui-plane',
          { width: 2, height: 3 },
          scene
        );
        this.worldSpacePlane.parent = this.sceneObject.transform;

        this.advancedTexture = AdvancedDynamicTexture.CreateForMesh(
          this.worldSpacePlane,
          512,
          768
        );
        break;
      }
    }
  }

  /**
   * Create the root panel container
   */
  private createRootPanel(width: number): Rectangle {
    const panel = new Rectangle('autoui-root');
    panel.width = `${width}px`;
    panel.adaptHeightToChildren = true;
    panel.thickness = 1;
    panel.cornerRadius = 8;
    panel.background = 'rgba(30, 30, 30, 0.95)';
    panel.color = '#555';
    panel.isPointerBlocker = true;

    // Position based on renderMode
    const renderMode = this._resolved.renderMode ?? 'ScreenSpaceOverlay';
    if (renderMode !== 'WorldSpace') {
      panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
      panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;

      const pos = this._resolved.position ?? { x: 20, y: 20 };
      const safeArea = this.sceneObject.traceScene.safeAreaInsets;
      panel.width = `${this.screenPanelWidth(width)}px`;
      panel.left = pos.x + safeArea.left;
      panel.top = pos.y + safeArea.top;
    }

    return panel;
  }

  /**
   * Add a section for a single SceneObject's labeled items
   */
  private addObjectSection(parent: StackPanel, item: DiscoveredItem): void {
    // Section header with object name — use a wrapper so padding doesn't clip text
    const headerWrapper = new Rectangle(`section-header-${item.source.name}`);
    headerWrapper.width = '100%';
    headerWrapper.adaptHeightToChildren = true;
    headerWrapper.thickness = 0;
    headerWrapper.paddingTop = '12px';
    headerWrapper.paddingBottom = '6px';

    const header = this.createLabel(item.source.name, 14, true);
    header.color = '#888';
    header.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    headerWrapper.addControl(header);
    parent.addControl(headerWrapper);

    // Add property controls
    for (const prop of item.properties) {
      const control = this.createPropertyControl(prop, item.source);
      if (control) {
        parent.addControl(control);
      }
    }

    // Add action controls — render prev/next pairs as a cycler [<] Label [>]
    const prevAction = item.actions.find(a => a.name === 'prev');
    const nextAction = item.actions.find(a => a.name === 'next');
    if (prevAction && nextAction) {
      parent.addControl(this.createCyclerControl(prevAction, nextAction, item.source));
    }
    // Render any remaining actions as buttons
    for (const action of item.actions) {
      if (action === prevAction || action === nextAction) continue;
      parent.addControl(this.createActionButton(action));
    }
  }

  // ============================================================================
  // ACTION CONTROLS
  // ============================================================================

  private createCyclerControl(prevAction: DiscoveredAction, nextAction: DiscoveredAction, source: SceneObject): Control {
    const container = new Rectangle(`cycler-${source.name}`);
    container.height = '35px';
    container.paddingBottom = '5px';
    container.width = '100%';
    container.thickness = 0;

    const leftBtn = Button.CreateSimpleButton('cycler-prev', '<');
    leftBtn.width = '30px';
    leftBtn.height = '28px';
    leftBtn.color = 'white';
    leftBtn.background = '#4a9eff';
    leftBtn.cornerRadius = 4;
    leftBtn.fontSize = 14;
    leftBtn.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    leftBtn.hoverCursor = 'pointer';

    // Center label — shows active page name, updated after each prev/next
    const nameLabel = new TextBlock(`cycler-label-${source.name}`);
    nameLabel.color = 'white';
    nameLabel.fontSize = 12;
    nameLabel.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    nameLabel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;

    const updateLabel = () => {
      const pages = source.getBehavior(Pages);
      if (pages) {
        const nodes = pages.getPageNodes();
        const idx = pages.getActivePageIndex();
        nameLabel.text = nodes[idx]?.name ?? '';
      }
    };
    updateLabel();

    // Track for live updates (autoplay, external changes)
    this.trackedControls.push({
      source,
      propName: '__pages_active',
      propType: 'cyclerLabel',
      update: () => updateLabel(),
    });

    leftBtn.onPointerUpObservable.add(() => {
      prevAction.execute();
      updateLabel();
    });

    const rightBtn = Button.CreateSimpleButton('cycler-next', '>');
    rightBtn.width = '30px';
    rightBtn.height = '28px';
    rightBtn.color = 'white';
    rightBtn.background = '#4a9eff';
    rightBtn.cornerRadius = 4;
    rightBtn.fontSize = 14;
    rightBtn.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    rightBtn.hoverCursor = 'pointer';
    rightBtn.onPointerUpObservable.add(() => {
      nextAction.execute();
      updateLabel();
    });

    // Label first so buttons layer on top and receive pointer events
    container.addControl(nameLabel);
    container.addControl(leftBtn);
    container.addControl(rightBtn);

    return container;
  }

  private createActionButton(action: DiscoveredAction): Control {
    const button = Button.CreateSimpleButton(`action-${action.name}`, action.label);
    button.width = '100%';
    button.height = '30px';
    button.color = 'white';
    button.background = '#4a9eff';
    button.cornerRadius = 4;
    button.paddingTop = '3px';
    button.paddingBottom = '3px';
    button.hoverCursor = 'pointer';

    button.onPointerUpObservable.add(() => {
      action.execute();
      Logger.debug(`[AutoUI] Executed action: ${action.name}`);
    });

    return button;
  }

  // ============================================================================
  // PROPERTY CONTROLS
  // ============================================================================

  /**
   * Create a control for a property based on its type
   */
  /** Runtime controls reflect resolved values, displayed in declared units. */
  private _getPropertyValue(name: string, source: SceneObject): PropertyValue | undefined {
    const properties = source.getBehavior(Properties);
    const declaration = properties?.getProperty(name);
    return source.traceScene.isPlayMode()
      ? fromCanonical(declarationUnits(declaration), properties?.getValue(name))
      : declaration?.value;
  }

  /**
   * The declared unit's short suffix (`ft`, `m`), or undefined when the
   * declaration carries no `units`. Runtime resolved values are converted
   * back from canonical metres for display. CANOPY_UNITS §7.1.
   */
  private unitFor(prop: PropertyDeclaration): string | undefined {
    return unitSuffix(declarationUnits(prop));
  }

  /** Label text, with the declared unit appended for bare-number editors. */
  private labelFor(prop: PropertyDeclaration): string {
    const base = prop.label ?? prop.name;
    const wantsUnitInLabel = prop.type === 'vec3'
      || (prop.type === 'number' && prop.uiHint === 'input');
    const unit = wantsUnitInLabel ? this.unitFor(prop) : undefined;
    return unit ? `${base} (${unit})` : base;
  }

  private createPropertyControl(prop: PropertyDeclaration, source: SceneObject): Control | null {
    // Use Rectangle with absolute positioning instead of horizontal StackPanel.
    // StackPanel nesting causes Slider hit-test coordinate misalignment.
    const container = new Rectangle(`prop-${prop.name}`);
    container.height = '30px';
    container.paddingBottom = '5px';
    container.width = '100%';
    container.thickness = 0;

    // Label. Controls whose value box is a bare editable number (the `input`
    // uiHint, vec3 axes) carry the declared unit here — putting it inside the
    // box would fight the user's own typing. CANOPY_UNITS §7.1.
    const label = this.createLabel(this.labelFor(prop), 12);
    label.width = '35%';
    label.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    label.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    container.addControl(label);

    // Value control based on type
    let valueControl: Control | null = null;

    switch (prop.type) {
      case 'string':
        valueControl = this.createStringInput(prop, source);
        break;
      case 'number':
        valueControl = this.createNumberInput(prop, source);
        break;
      case 'boolean':
        valueControl = this.createBooleanInput(prop, source);
        break;
      case 'color':
        valueControl = prop.variants?.length
          ? this.createSwatchRow(prop, source)
          : this.createColorInput(prop, source);
        break;
      case 'vec3':
        valueControl = this.createVector3Input(prop, source);
        break;
    }

    if (valueControl) {
      // Left-align checkboxes next to the label; right-align everything else
      const isCheckbox = prop.type === 'boolean';
      valueControl.horizontalAlignment = isCheckbox
        ? Control.HORIZONTAL_ALIGNMENT_LEFT
        : Control.HORIZONTAL_ALIGNMENT_RIGHT;
      if (isCheckbox) {
        valueControl.left = '35%';
      }
      container.addControl(valueControl);
    }

    return container;
  }

  private createStringInput(prop: PropertyDeclaration, source: SceneObject): Control {
    const input = new InputText(`input-${prop.name}`);
    input.width = '65%';
    input.height = '25px';
    input.color = 'white';
    input.background = '#444';
    input.focusedBackground = '#555';
    input.hoverCursor = 'text';
    input.onFocusSelectAll = true;
    const strVal = this._getPropertyValue(prop.name, source);
    input.text = typeof strVal === 'string' ? strVal : '';

    let updatingProgrammatically = false;
    input.onTextChangedObservable.add(() => {
      if (updatingProgrammatically) return;
      this.setPropertyValue(source, prop.name, input.text);
    });

    const isFocused = this.trackInputFocus(input);

    // Track for live updates
    this.trackControl(source, prop.name, 'string', (value) => {
      if (!isFocused()) {
        try {
          updatingProgrammatically = true;
          input.text = typeof value === 'string' ? value : '';
        } finally {
          updatingProgrammatically = false;
        }
      }
    });

    return input;
  }

  private createNumberInput(prop: PropertyDeclaration, source: SceneObject): Control {
    // Cast to NumberDeclaration to access extended fields
    const numProp = prop as NumberDeclaration;

    // Check for text input uiHint
    if (numProp.uiHint === 'input') {
      return this.createNumberTextInput(numProp, source);
    }

    // Slider with value text overlay
    const container = new Rectangle(`number-container-${prop.name}`);
    container.width = '65%';
    container.height = '20px';
    container.thickness = 0;

    const slider = new Slider(`slider-${prop.name}`);
    slider.minimum = numProp.min ?? 0;
    slider.maximum = numProp.max ?? 100;
    slider.step = numProp.step ?? 0;
    slider.value = Number(this._getPropertyValue(prop.name, source) ?? 0);
    slider.height = '20px';
    slider.color = '#4a9eff';
    slider.background = '#444';
    slider.hoverCursor = 'pointer';

    // Value display overlaid on the right — pass-through for pointer events.
    // The readout carries the declared unit (`3.0 ft`); the slider's own
    // min/max/step/value stay bare declared-space numbers. CANOPY_UNITS §7.1.
    const unit = this.unitFor(prop);
    const readout = (v: number): string => {
      const number = formatSliderNumber(v, slider.minimum, slider.maximum, slider.step);
      return unit ? `${number} ${unit}` : number;
    };
    const valueText = new TextBlock(`value-${prop.name}`);
    valueText.text = readout(Number(this._getPropertyValue(prop.name, source) ?? 0));
    valueText.width = unit ? '65px' : '45px';
    valueText.color = 'white';
    valueText.fontSize = 11;
    valueText.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    valueText.isHitTestVisible = false;

    let updatingSlider = false;
    slider.onValueChangedObservable.add((value: number) => {
      if (updatingSlider) return;
      valueText.text = readout(value);
      this.setPropertyValue(source, prop.name, value);
    });

    // Add slider first, then value text on top
    container.addControl(slider);
    container.addControl(valueText);

    // Track for live updates
    this.trackControl(source, prop.name, 'number', (value) => {
      const numValue = Number(value ?? 0);
      try {
        updatingSlider = true;
        slider.value = numValue;
      } finally {
        updatingSlider = false;
      }
      valueText.text = readout(numValue);
    });

    return container;
  }

  private createNumberTextInput(prop: NumberDeclaration, source: SceneObject): Control {
    const input = new InputText(`input-${prop.name}`);
    input.width = '65%';
    input.height = '25px';
    input.color = 'white';
    input.background = '#444';
    input.focusedBackground = '#555';
    input.hoverCursor = 'text';
    input.onFocusSelectAll = true;
    const numVal = this._getPropertyValue(prop.name, source);
    input.text = String(typeof numVal === 'number' ? numVal : 0);

    let updatingProgrammatically = false;
    input.onTextChangedObservable.add(() => {
      if (updatingProgrammatically) return;
      const num = parseFloat(input.text);
      if (!isNaN(num)) {
        this.setPropertyValue(source, prop.name, num);
      }
    });

    const isFocused = this.trackInputFocus(input);

    // Track for live updates
    this.trackControl(source, prop.name, 'number', (value) => {
      if (!isFocused()) {
        try {
          updatingProgrammatically = true;
          input.text = typeof value === 'number' ? String(value) : '0';
        } finally {
          updatingProgrammatically = false;
        }
      }
    });

    return input;
  }

  private createBooleanInput(prop: PropertyDeclaration, source: SceneObject): Control {
    const checkbox = new Checkbox(`checkbox-${prop.name}`);
    checkbox.width = '20px';
    checkbox.height = '20px';
    checkbox.isChecked = Boolean(this._getPropertyValue(prop.name, source) ?? false);
    checkbox.color = '#4a9eff';
    checkbox.background = '#444';
    checkbox.hoverCursor = 'pointer';

    let updatingProgrammatically = false;
    checkbox.onIsCheckedChangedObservable.add((checked) => {
      if (updatingProgrammatically) return;
      this.setPropertyValue(source, prop.name, checked);
    });

    // Track for live updates
    this.trackControl(source, prop.name, 'boolean', (value) => {
      try {
        updatingProgrammatically = true;
        checkbox.isChecked = Boolean(value ?? false);
      } finally {
        updatingProgrammatically = false;
      }
    });

    return checkbox;
  }

  private createColorInput(prop: PropertyDeclaration, source: SceneObject): Control {
    const container = new StackPanel(`color-container-${prop.name}`);
    container.isVertical = false;
    container.width = '65%';

    // Color picker
    const picker = new ColorPicker(`colorpicker-${prop.name}`);
    picker.width = '80px';
    picker.height = '80px';
    picker.hoverCursor = 'pointer';

    // Parse initial color
    const colorVal = this._getPropertyValue(prop.name, source);
    const colorStr = typeof colorVal === 'string' ? colorVal : '#ffffff';
    const color = Color3.FromHexString(colorStr.substring(0, 7));
    picker.value = color;

    // Hex input
    const hexInput = new InputText(`hex-${prop.name}`);
    hexInput.width = '80px';
    hexInput.height = '25px';
    hexInput.color = 'white';
    hexInput.background = '#444';
    hexInput.hoverCursor = 'text';
    hexInput.onFocusSelectAll = true;
    hexInput.text = colorStr;

    let updatingProgrammatically = false;
    picker.onValueChangedObservable.add((newColor) => {
      if (updatingProgrammatically) return;
      const hex = newColor.toHexString();
      hexInput.text = hex;
      this.setPropertyValue(source, prop.name, hex);
    });

    hexInput.onTextChangedObservable.add(() => {
      if (updatingProgrammatically) return;
      if (/^#[0-9A-Fa-f]{6}$/.test(hexInput.text)) {
        picker.value = Color3.FromHexString(hexInput.text);
        this.setPropertyValue(source, prop.name, hexInput.text);
      }
    });

    container.addControl(picker);
    container.addControl(hexInput);

    const isFocused = this.trackInputFocus(hexInput);

    // Track for live updates
    this.trackControl(source, prop.name, 'color', (value) => {
      const hex = typeof value === 'string' ? value : '#ffffff';
      try {
        updatingProgrammatically = true;
        if (!isFocused()) {
          hexInput.text = hex;
        }
        picker.value = Color3.FromHexString(hex.substring(0, 7));
      } catch {
        // Ignore invalid colors
      } finally {
        updatingProgrammatically = false;
      }
    });

    return container;
  }

  /**
   * Swatch-row control for a color property that declares `variants`. Renders one
   * chip per variant; the chip whose value matches the current property value gets
   * a thicker border ring. Variants are suggestions, not a closed set — no match
   * means no ring.
   *
   * Write path depends on mode:
   * - PLAY mode → `Properties.setValue` (runtime tier, VARIANTS_DESIGN §5 G3b):
   *   never a commit — a commit cancels in-flight transitions. `setValue`
   *   picks up the declaration's default `transition`, so the swatch animates.
   * - EDIT mode → the editor-injected {@link setAutoUISwatchEditorCommit} hook
   *   (if a host installed one): the pick commits into the active state (or the
   *   declaration base) with undo — parity with the behaviors panel, so thumbnails
   *   and authored data stay consistent. Falls back to `setValue` when no hook.
   */
  private createSwatchRow(prop: Extract<PropertyDeclaration, { type: 'color' }>, source: SceneObject): Control {
    const container = new StackPanel(`swatch-container-${prop.name}`);
    container.isVertical = false;
    container.width = '65%';

    const variants = prop.variants ?? [];
    const normalize = (v: unknown): string =>
      typeof v === 'string' ? v.substring(0, 7).toLowerCase() : '';

    // Build one chip per variant; keep refs so the live-update callback can re-ring.
    const chips: { rect: Rectangle; value: string }[] = [];
    for (const variant of variants) {
      const chip = new Rectangle(`swatch-${prop.name}-${variant.name}`);
      chip.width = '22px';
      chip.height = '22px';
      chip.thickness = 1;
      chip.color = '#000';
      chip.background = variant.value;
      chip.hoverCursor = 'pointer';
      // Keep the constructor's unique `swatch-<prop>-<variant>` name (don't
      // overwrite with the bare variant name — two rows could share one, and it
      // aids debugging). No tooltip UI is wired up today.

      chip.onPointerUpObservable.add(() => {
        this._writeSwatchValue(source, prop.name, variant.value);
      });

      container.addControl(chip);
      chips.push({ rect: chip, value: normalize(variant.value) });
    }

    const applyRing = (current: unknown): void => {
      const cur = normalize(current);
      for (const { rect, value } of chips) {
        const matches = cur !== '' && value === cur;
        rect.thickness = matches ? 3 : 1;
        rect.color = matches ? '#fff' : '#000';
      }
    };
    // Ring the RESOLVED value (override / active state / inherited), so both a
    // play-mode setValue and an edit-mode state commit move the highlight.
    applyRing(source.getBehavior(Properties)?.getValue(prop.name));

    // Live updates: re-ring on value changes (resolved value — see the
    // 'colorResolved' branch in updateTrackedControls).
    let updatingProgrammatically = false;
    this.trackControl(source, prop.name, 'colorResolved', (value) => {
      if (updatingProgrammatically) return;
      try {
        updatingProgrammatically = true;
        applyRing(value);
      } finally {
        updatingProgrammatically = false;
      }
    });

    return container;
  }

  /**
   * Write a variant-swatch pick. PLAY mode (or no editor host) → runtime-tier
   * `setValue` (animates via the declaration transition, leaves the state stack
   * intact, §5 G3b). EDIT mode with an installed host → the editor commit hook
   * (lands in the active state / declaration base, with undo — behaviors-panel
   * parity).
   */
  private _writeSwatchValue(source: SceneObject, propName: string, value: PropertyValue): void {
    const inPlayMode = source.traceScene.isPlayMode();
    if (!inPlayMode && swatchEditorCommit) {
      swatchEditorCommit(source, propName, value);
    } else {
      source.getBehavior(Properties)?.setValue(propName, value);
    }
  }

  private createVector3Input(prop: PropertyDeclaration, source: SceneObject): Control {
    const container = new StackPanel(`vec3-container-${prop.name}`);
    container.isVertical = false;
    container.width = '65%';

    const vec = (this._getPropertyValue(prop.name, source) as [number, number, number]) ?? [0, 0, 0];
    const axisLabels = ['X', 'Y', 'Z'];
    const colors = ['#f55', '#5f5', '#55f'];

    // Store inputs for live updates
    const inputs: { input: InputText; index: number; isFocused: () => boolean }[] = [];

    for (let i = 0; i < 3; i++) {
      const axisLabel = new TextBlock(`${axisLabels[i].toLowerCase()}-label-${prop.name}`);
      axisLabel.text = axisLabels[i];
      axisLabel.width = '14px';
      axisLabel.color = colors[i];
      axisLabel.fontSize = 10;
      container.addControl(axisLabel);

      const input = new InputText(`${axisLabels[i].toLowerCase()}-input-${prop.name}`);
      input.width = '45px';
      input.height = '20px';
      input.color = 'white';
      input.background = '#444';
      input.hoverCursor = 'text';
      input.onFocusSelectAll = true;
      input.text = String(vec[i]);

      const isFocused = this.trackInputFocus(input);
      inputs.push({ input, index: i, isFocused });

      const idx = i; // capture for closure
      input.onTextChangedObservable.add(() => {
        if (updatingVec3) return;
        const newVec: [number, number, number] = [...vec];
        newVec[idx] = parseFloat(input.text) || 0;
        this.setPropertyValue(source, prop.name, newVec);
      });

      container.addControl(input);
    }

    let updatingVec3 = false;
    // Track for live updates
    this.trackControl(source, prop.name, 'vec3', (value) => {
      const newVec = (value as [number, number, number]) ?? [0, 0, 0];
      try {
        updatingVec3 = true;
        for (const { input, index, isFocused } of inputs) {
          if (!isFocused()) {
            input.text = String(newVec[index]);
          }
        }
      } finally {
        updatingVec3 = false;
      }
    });

    return container;
  }


  // ============================================================================
  // SAFE AREA
  // ============================================================================

  private _subscribeSafeArea(): void {
    const evt = this.sceneObject.traceScene.getEventSystem();
    if (!evt) return;

    this._safeAreaHandler = () => this._updatePanelPosition();
    evt.addEventListener('trace-viewer:safe-area-changed', this._safeAreaHandler);
  }

  private _unsubscribeSafeArea(): void {
    if (!this._safeAreaHandler) return;
    const evt = this.sceneObject.traceScene.getEventSystem();
    if (evt) {
      evt.removeEventListener('trace-viewer:safe-area-changed', this._safeAreaHandler);
    }
    this._safeAreaHandler = null;
  }

  /** Keep authored screen panels inside the CSS viewport, including on resize. */
  private screenPanelWidth(width: number): number {
    const engine = this.sceneObject.babylonScene.getEngine();
    const cssWidth = engine.getRenderWidth() * engine.getHardwareScalingLevel();
    const safe = this.sceneObject.traceScene.safeAreaInsets;
    const margin = Math.max(0, this._resolved.position?.x ?? 20);
    return Math.max(1, Math.min(width, cssWidth - safe.left - safe.right - margin * 2));
  }

  /** Reposition the root panel when safe area changes */
  private _updatePanelPosition(): void {
    if (!this.rootPanel) return;
    const renderMode = this._resolved.renderMode ?? 'ScreenSpaceOverlay';
    if (renderMode === 'WorldSpace') return;

    const pos = this._resolved.position ?? { x: 20, y: 20 };
    const safeArea = this.sceneObject.traceScene.safeAreaInsets;
    this.rootPanel.width = `${this.screenPanelWidth(this._resolved.width ?? 300)}px`;
    this.rootPanel.left = pos.x + safeArea.left;
    this.rootPanel.top = pos.y + safeArea.top;
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  private createLabel(text: string, fontSize: number, bold = false): TextBlock {
    const label = new TextBlock();
    label.text = text;
    label.color = 'white';
    label.fontSize = fontSize;
    label.fontWeight = bold ? 'bold' : 'normal';
    label.resizeToFit = true;
    label.textWrapping = true;
    return label;
  }

  /**
   * Wire up focus/blur tracking for a Babylon GUI InputText.
   * Returns a getter for the current focus state so callers can
   * skip live-update writes while the user is typing.
   */
  private trackInputFocus(input: InputText): () => boolean {
    let focused = false;
    input.onFocusObservable.add(() => {
      focused = true;
      this.sceneObject.traceScene.guiFocusAcquired();
    });
    input.onBlurObservable.add(() => {
      focused = false;
      this.sceneObject.traceScene.guiFocusReleased();
    });
    return () => focused;
  }

  private setPropertyValue(source: SceneObject, propName: string, value: PropertyValue): void {
    const properties = source.getBehavior(Properties);
    if (!properties) return;

    // Match graph actions and variant swatches: play controls override runtime
    // state without editing the saved scene. This also lets a control replace
    // a graph-written value after Reset instead of writing underneath it.
    if (source.traceScene.isPlayMode()) {
      properties.setValue(propName, value);
      return;
    }

    // Commit the new value into the authored declaration so it flows through
    // the normal inheritance cascade. Using setValue() would create a runtime
    // override that bypasses inheritance and pins the property.
    const decl = properties.getProperty(propName);
    if (!decl) return;

    const updated = { ...decl, value } as PropertyDeclaration;
    const allDecls = properties.getProperties().map(d => d.name === propName ? updated : d);
    void properties.commit({ properties: allDecls });
    Logger.debug(`[AutoUI] Set ${source.name}.${propName} = ${JSON.stringify(value)}`);
  }

  /**
   * Compute a fingerprint representing the current discoverable structure.
   * Any change to a property definition (type, min, max, step, uiHint, label, …)
   * will produce a different fingerprint and trigger a full UI rebuild.
   */
  private computeStructureFingerprint(): string {
    const descendants = this.getAllDescendantSceneObjects();
    const parts: string[] = [];
    for (const child of descendants) {
      // Same filter as discovery: a node whose only declarations are hidden
      // renders nothing, so it must not shape the structural key either.
      const props = (child.getBehavior(Properties)?.getProperties() ?? []).filter(p => !p.hidden);

      // Lightweight action key: just check which behavior types have actions in their schema.
      // Avoids constructing DiscoveredAction objects with closures on every frame.
      const actionTypes: string[] = [];
      for (const behavior of child.getBehaviors()) {
        const schema = SCHEMA_BY_TYPE.get(behavior.name);
        if (schema?.actions) actionTypes.push(behavior.name);
      }

      if (props.length > 0 || actionTypes.length > 0) {
        const propKeys = props.map(p => {
          const { value: _v, ...def } = p as unknown as Record<string, unknown>;
          return JSON.stringify(def);
        }).join(',');
        parts.push(`${child.name}:p(${propKeys}):a(${actionTypes.join(',')})`);
      }
    }
    return parts.join('|');
  }

  /**
   * Poll each frame for structure changes — new Properties/behaviors
   * being added, or property definitions being edited.
   */
  private startStructurePolling(): void {
    this.stopStructurePolling();
    this.structureFingerprint = this.computeStructureFingerprint();
    this.structureObserver = this.sceneObject.babylonScene
      .onBeforeRenderObservable.add(() => {
        const current = this.computeStructureFingerprint();
        if (current !== this.structureFingerprint) {
          this.structureFingerprint = current;
          this.disposeUI();
          this.buildUI();
        }
      });
  }

  private stopStructurePolling(): void {
    if (this.structureObserver) {
      this.sceneObject.babylonScene.onBeforeRenderObservable.remove(this.structureObserver);
      this.structureObserver = null;
    }
  }

  private disposeUI(): void {
    this.readoutControl = null;
    this.updateCollapse = null;
    this.stopFrameUpdates();
    this.trackedControls = [];

    if (this.advancedTexture) {
      // Clear focused control before disposal so blur observers fire
      // and the TraceScene guiFocusCount is properly decremented.
      this.advancedTexture.focusedControl = null;
      this.advancedTexture.dispose();
      this.advancedTexture = null;
    }
    if (this.worldSpacePlane) {
      this.worldSpacePlane.dispose();
      this.worldSpacePlane = null;
    }
    this.rootPanel = null;
  }

  // ============================================================================
  // FRAME UPDATES
  // ============================================================================

  /**
   * Start updating tracked controls on each frame
   */
  private startFrameUpdates(): void {
    if (this.renderObserver !== null) return;

    const scene = this.sceneObject.babylonScene;
    this.renderObserver = scene.onBeforeRenderObservable.add(() => {
      this.updateTrackedControls();
    });
  }

  /**
   * Stop frame updates
   */
  private stopFrameUpdates(): void {
    if (this.renderObserver !== null) {
      const scene = this.sceneObject.babylonScene;
      scene.onBeforeRenderObservable.remove(this.renderObserver);
      this.renderObserver = null;
    }
  }

  /**
   * Sync screen-space overlay visibility with the transform's enabled state.
   * WorldSpace mode doesn't need this — the plane is parented to the transform.
   */
  private updateVisibility(): void {
    if (!this.rootPanel) return;
    const renderMode = this._resolved.renderMode ?? 'ScreenSpaceOverlay';
    if (renderMode === 'WorldSpace') return;

    this.rootPanel.isVisible = this.sceneObject.transform.isEnabled();
  }

  /**
   * Keep idealWidth in sync with the canvas so pixel-based GUI sizes stay
   * correct after the engine resizes (e.g. viewer-app goes from 1x1px
   * preload container to full-screen).
   */
  private updateIdealWidth(): void {
    if (!this.advancedTexture) return;
    const renderMode = this._resolved.renderMode ?? 'ScreenSpaceOverlay';
    if (renderMode === 'WorldSpace') return;

    const engine = this.sceneObject.babylonScene.getEngine();
    const cssWidth = engine.getRenderWidth() * engine.getHardwareScalingLevel();
    if (cssWidth !== this._lastCssWidth && cssWidth > 1) {
      this._lastCssWidth = cssWidth;
      this.advancedTexture.idealWidth = cssWidth;
      this._updatePanelPosition();
    }
  }

  /**
   * Update all tracked controls with current property values
   */
  private updateTrackedControls(): void {
    this.updateIdealWidth();
    this.updateVisibility();

    for (const tracked of this.trackedControls) {
      if (tracked.propType === 'cyclerLabel') {
        tracked.update(undefined);
        continue;
      }
      const properties = tracked.source.getBehavior(Properties);
      if (properties) {
        // Swatch rings must reflect the RESOLVED value (runtime override / active
        // state / inherited), not just the authored declaration default — a
        // setValue pick or a state override would otherwise never move the ring.
        const currentValue = tracked.propType === 'colorResolved'
          ? properties.getValue(tracked.propName)
          : this._getPropertyValue(tracked.propName, tracked.source);
        tracked.update(currentValue);
      }
    }
  }

  /**
   * Register a control for live value updates
   */
  private trackControl(
    source: SceneObject,
    propName: string,
    propType: string,
    update: (value: PropertyValue | undefined) => void
  ): void {
    this.trackedControls.push({ source, propName, propType, update });
  }
}
